<template>
  <div class="bartender-printer">
    <!-- BarTender打印配置面板 -->
    <div v-if="showConfig" class="config-panel">
      <h3>BarTender热敏标签打印配置</h3>
      
      <div class="config-section">
        <label>标签模板：</label>
        <div class="file-input-container">
          <input
              v-model="bartenderConfig.templatePath"
              type="text"
              placeholder="BarTender模板文件路径(.btw)"
              class="config-input"
              readonly>
          <input 
              ref="templateFileInput"
              type="file" 
              accept=".btw" 
              style="display: none" 
              @change="handleTemplateFileSelect"
          >
          <button type="button" class="btn-file-select" @click="selectTemplateFile">
            选择文件
          </button>
        </div>
      </div>
      
      <div class="config-section">
        <label>打印机名称：</label>
        <select v-model="bartenderConfig.printerName" class="config-select">
          <option value="">请选择打印机</option>
          <option 
              v-for="printer in availablePrinters" 
              :key="printer.uid" 
              :value="printer.name"
          >
            {{printer.name}} ({{printer.connection}})
          </option>
        </select>
      </div>
      
      <div class="config-section">
        <label>标签尺寸：</label>
        <select v-model="bartenderConfig.labelSize" class="config-select">
          <option value="50x30">50mm × 30mm</option>
          <option value="60x40">60mm × 40mm</option>
          <option value="70x50">70mm × 50mm</option>
          <option value="80x60">80mm × 60mm</option>
        </select>
      </div>
      
      <div class="config-section">
        <label>打印份数：</label>
        <input
            v-model.number="bartenderConfig.copies"
            type="number"
            min="1"
            max="10"
            class="config-input">
      </div>
      
      <div class="config-section checkbox-group">
        <label>
          <input v-model="bartenderConfig.usePreview" type="checkbox" @change="previewLabels"> 
          打印前预览
        </label>
        <label>
          <input v-model="bartenderConfig.autoPrint" type="checkbox"> 
          自动打印
        </label>
      </div>
      
      <div class="config-section">
        <label>数据字段映射：</label>
        <div class="field-mapping">
          <div v-for="field in fieldMappings" :key="field.key" class="field-item">
            <span>{{field.label}}：</span>
            <input v-model="field.source" type="text" :placeholder="'绑定字段名'" class="field-input">
          </div>
        </div>
      </div>
      
      <div class="config-actions">
        <button :disabled="testing" class="btn-test" @click="testConnection">
          {{testing ? '测试中...' : '测试连接'}}
        </button>
        <button class="btn-save" @click="saveConfig">保存配置</button>
        <button class="btn-cancel" @click="cancelConfig">取消</button>
      </div>
    </div>
    
    <!-- 打印预览区域 -->
    <div v-if="showPreview" class="preview-panel">
      <h3>标签预览</h3>
      <div class="preview-container">
        <div v-for="(item, index) in printData" :key="index" class="preview-item">
          <div class="preview-label">
            <div class="preview-qr">
              <div :id="'preview-qr-' + index" class="qr-code"/>
            </div>
            <div class="preview-content">
              <div class="preview-text">{{getFieldValue(item, 'barcode')}}</div>
              <div class="preview-text">{{getFieldValue(item, 'billNo')}}</div>
              <div class="preview-text">{{getFieldValue(item, 'terminalCustomerName')}}</div>
              <div class="preview-text">{{getFieldValue(item, 'receiveTime')}}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="preview-actions">
        <button class="btn-confirm" @click="confirmPrint">确认打印</button>
        <button class="btn-cancel" @click="cancelPreview">取消</button>
      </div>
    </div>
    
    <!-- 打印状态显示 -->
    <div v-if="printing" class="printing-status">
      <div class="spinner"/>
      <p>正在通过BarTender打印标签...</p>
      <p>已打印: {{printedCount}} / {{totalCount}}</p>
    </div>
    
    <!-- 打印结果反馈 -->
    <div v-if="printResult" class="print-result" :class="printResult.type">
      <p>{{printResult.message}}</p>
      <button class="btn-clear" @click="clearResult">关闭</button>
    </div>
  </div>
</template>

<script>
  import QRCode from "qrcodejs2";

  export default {
    name: "BarTenderPrinter",
    props: {
      // 打印数据
      printData: {
        type: Array,
        default: () => [],
      },
      // 是否显示配置面板
      showConfig: {
        type: Boolean,
        default: false,
      },
    },
  
    data() {
      return {
        // BarTender配置
        bartenderConfig: {
          templatePath: "",
          printerName: "",
          labelSize: "50x30",
          copies: 1,
          usePreview: true,
          autoPrint: false,
          activeXEnabled: false,
          websocketUrl: "",
        },
      
        // 字段映射
        fieldMappings: [
          { key: "barcode", label: "条码", source: "barcode" },
          { key: "billNo", label: "单号", source: "billNo" },
          { key: "terminalCustomerName", label: "客户名称", source: "terminalCustomerName" },
          { key: "receiveTime", label: "接收时间", source: "receiveTime" },
        ],
      
        // 状态变量
        testing: false,
        showPreview: false,
        printing: false,
        printedCount: 0,
        totalCount: 0,
        printResult: null,
      
        // BrowserPrint实例
        browserPrint: null,
        availablePrinters: [],
        selectedPrinter: null,
      };
    },
  
    mounted() {
      this.initBrowserPrint();
      this.loadSavedConfig();
    },
  
    methods: {
      // 初始化BrowserPrint
      async initBrowserPrint() {
        try {
          console.log("尝试初始化BrowserPrint...", BrowserPrint);
          if (typeof BrowserPrint !== "undefined") {
            // BrowserPrint 不需要通过 new 构造，直接使用
            this.browserPrint = BrowserPrint;
            this.bartenderConfig.activeXEnabled = true;
            
            // 获取可用打印机列表
            this.getAvailablePrinters();
          } else {
            console.warn("BrowserPrint库未加载，请确保BrowserPrint-3.0.216.min.js已正确引入");
          }
        } catch (error) {
          console.error("初始化BrowserPrint失败:", error);
        }
      },
    
      // 获取可用打印机列表
      getAvailablePrinters() {
        if (this.browserPrint) {
          try {
            // 先检查是否可以通过默认方法获取设备
            this.browserPrint.getLocalDevices((devices) => {
              try {
                // 过滤出打印机设备
                const printers = devices.filter ? 
                  devices.filter(device => device.deviceType === "printer") : 
                  devices;
                
                this.availablePrinters = printers || [];
                console.log("可用打印机:", this.availablePrinters);
                
                // 默认选择第一个热敏打印机
                if (printers && printers.length > 0) {
                  const thermalPrinter = printers.find(p => 
                    p.name.toLowerCase().includes("thermal") || 
                    p.name.toLowerCase().includes("标签") ||
                    p.name.toLowerCase().includes("label")
                  );
                  if (thermalPrinter) {
                    this.bartenderConfig.printerName = thermalPrinter.name;
                  } else {
                    this.bartenderConfig.printerName = printers[0].name;
                  }
                }
              } catch (error) {
                console.error("处理设备列表失败:", error);
                this.availablePrinters = [];
              }
            }, (error) => {
              console.error("获取打印机列表失败:", error);
              this.showResult("warning", "无法获取打印机列表，请确保BrowserPrint服务正在运行");
              this.availablePrinters = [];
            });
          } catch (error) {
            console.error("初始化打印机列表失败:", error);
            this.showResult("error", "初始化打印机列表失败: " + error.message);
            this.availablePrinters = [];
          }
        } else {
          this.showResult("warning", "BrowserPrint库未加载，请检查是否正确引入");
          this.availablePrinters = [];
        }
      },
    
      // 测试BarTender连接
      async testConnection() {
        this.testing = true;
        try {
          // 检查BarTender是否安装
          const bartenderInstalled = await this.checkBarTenderInstalled();
          if (!bartenderInstalled) {
            this.showResult("error", "BarTender软件未安装或未正确配置");
            return;
          }
        
          // 测试打印机连接
          if (this.bartenderConfig.printerName) {
            const printer = await this.testPrinterConnection(this.bartenderConfig.printerName);
            if (printer) {
              this.showResult("success", "BarTender连接测试成功！");
            } else {
              this.showResult("error", "打印机连接失败，请检查打印机名称");
            }
          } else {
            this.showResult("warning", "请先选择打印机");
          }
        } catch (error) {
          console.error("测试连接失败:", error);
          this.showResult("error", "连接测试失败: " + error.message);
        } finally {
          this.testing = false;
        }
      },
    
      // 检查BarTender是否安装
      async checkBarTenderInstalled() {
        try {
          console.log("尝试检查BarTender安装...", window.ActiveXObject);
          // 尝试通过ActiveX检查BarTender
          if (window.ActiveXObject || "ActiveXObject" in window) {
            const bt = new ActiveXObject("BarTender.Application");
            return bt !== null;
          }
          return false;
        } catch (error) {
          // 如果不是IE或ActiveX不可用，尝试其他方法
          console.log("ActiveX不可用，尝试其他检测方法");
          return false;
        }
      },
    
      // 测试打印机连接
      async testPrinterConnection(printerName) {
        return new Promise((resolve) => {
          if (this.browserPrint) {
            this.browserPrint.getLocalDevices((devices) => {
              const printers = devices.filter ? 
                devices.filter(device => device.deviceType === "printer") : 
                devices;
              const printer = printers.find(p => p.name === printerName);
              resolve(printer || null);
            }, (error) => {
              console.error("获取打印机列表失败:", error);
              resolve(null);
            });
          } else {
            resolve(null);
          }
        });
      },
    
      // 选择模板文件
      selectTemplateFile() {
        this.$refs.templateFileInput.click();
      },
    
      // 处理模板文件选择
      handleTemplateFileSelect(event) {
        const file = event.target.files[0];
        if (file) {
          if (file.name.toLowerCase().endsWith(".btw")) {
            // 使用用户选择的文件名
            this.bartenderConfig.templatePath = file.name;
            this.showResult("success", `已选择模板文件: ${file.name}`);
          } else {
            this.showResult("error", "请选择有效的BarTender模板文件(.btw)");
            // 清空选择
            event.target.value = "";
          }
        }
      },
    
      // 保存配置
      saveConfig() {
        try {
          localStorage.setItem("bartenderConfig", JSON.stringify(this.bartenderConfig));
          localStorage.setItem("bartenderFieldMappings", JSON.stringify(this.fieldMappings));
          this.showResult("success", "配置保存成功！");
          this.$emit("configSaved", {
            config: this.bartenderConfig,
            fieldMappings: this.fieldMappings,
          });
        } catch (error) {
          this.showResult("error", "保存配置失败: " + error.message);
        }
      },
    
      // 加载保存的配置
      loadSavedConfig() {
        try {
          const savedConfig = localStorage.getItem("bartenderConfig");
          const savedMappings = localStorage.getItem("bartenderFieldMappings");
        
          if (savedConfig) {
            this.bartenderConfig = { ...this.bartenderConfig, ...JSON.parse(savedConfig) };
          }
        
          if (savedMappings) {
            this.fieldMappings = JSON.parse(savedMappings);
          }
        } catch (error) {
          console.error("加载配置失败:", error);
        }
      },
    
      // 预览标签
      previewLabels() {
        if (!this.bartenderConfig.usePreview) {
          this.printLabels();
          return;
        }
      
        this.showPreview = true;
        this.$nextTick(() => {
          // 生成预览二维码
          this.printData.forEach((item, index) => {
            const qrElement = document.getElementById("preview-qr-" + index);
            if (qrElement) {
              qrElement.innerHTML = "";
              new QRCode(qrElement, {
                text: this.getFieldValue(item, "barcode"),
                width: 60,
                height: 60,
                colorDark: "#000000",
                colorLight: "#ffffff",
                correctLevel: QRCode.CorrectLevel.M,
              });
            }
          });
        });
      },
    
      // 获取字段值
      getFieldValue(item, fieldName) {
        const mapping = this.fieldMappings.find(m => m.key === fieldName);
        if (mapping && mapping.source) {
          return item[mapping.source] || "";
        }
        return item[fieldName] || "";
      },
    
      // 确认打印
      async confirmPrint() {
        this.showPreview = false;
        await this.printLabels();
      },
    
      // 取消预览
      cancelPreview() {
        this.showPreview = false;
        this.$emit("cancelPrint");
      },
    
      // 取消配置
      cancelConfig() {
        this.$emit("cancelConfig");
      },
    
      // 打印标签
      async printLabels() {
        if (!this.printData || this.printData.length === 0) {
          this.showResult("error", "没有可打印的数据");
          return;
        }
      
        this.printing = true;
        this.printedCount = 0;
        this.totalCount = this.printData.length * this.bartenderConfig.copies;
      
        try {
          // 生成打印数据
          const printJobs = this.generatePrintJobs();
        
          // 执行打印
          for (let i = 0; i < printJobs.length; i++) {
            await this.executePrintJob(printJobs[i]);
            this.printedCount++;
          }
        
          this.showResult("success", `成功打印 ${this.totalCount} 个标签`);
        } catch (error) {
          console.error("打印失败:", error);
          this.showResult("error", "打印失败: " + error.message);
        } finally {
          this.printing = false;
        }
      },
    
      // 生成打印任务
      generatePrintJobs() {
        const jobs = [];
      
        this.printData.forEach(item => {
          for (let i = 0; i < this.bartenderConfig.copies; i++) {
            jobs.push({
              data: this.preparePrintData(item),
              template: this.bartenderConfig.templatePath,
              printer: this.bartenderConfig.printerName,
            });
          }
        });
      
        return jobs;
      },
    
      // 准备打印数据
      preparePrintData(item) {
        const data = {};
        this.fieldMappings.forEach(mapping => {
          data[mapping.key] = this.getFieldValue(item, mapping.key);
        });
      
        // 添加时间戳
        data.printTime = new Date().toLocaleString("zh-CN");
        data.batchNo = "BT" + Date.now();
      
        return data;
      },
    
      // 执行打印任务
      async executePrintJob(job) {
        return new Promise((resolve, reject) => {
          try {
            // 方法1: 使用BrowserPrint直接打印
            if (this.browserPrint && this.selectedPrinter) {
              const printContent = this.formatForBrowserPrint(job.data);
              this.browserPrint.print(printContent, this.selectedPrinter, resolve, reject);
            } 
            // 方法2: 使用BarTender ActiveX
            else if (window.ActiveXObject || "ActiveXObject" in window) {
              this.printWithActiveX(job);
              resolve();
            }
            // 方法3: 降级到浏览器打印
            else {
              this.fallbackPrint(job.data);
              resolve();
            }
          } catch (error) {
            reject(error);
          }
        });
      },
    
      // 格式化BrowserPrint数据
      formatForBrowserPrint(data) {
        // 根据标签尺寸格式化内容
        const labelWidth = this.bartenderConfig.labelSize.split("x")[0];
        const labelHeight = this.bartenderConfig.labelSize.split("x")[1];
      
        return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>热敏标签</title>
          <style>
            @page {
              size: ${labelWidth}mm ${labelHeight}mm;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 1mm;
              font-family: Arial, sans-serif;
              font-size: 8pt;
              width: ${labelWidth - 2}mm;
              height: ${labelHeight - 2}mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .qr-code {
              width: 15mm;
              height: 15mm;
            }
            .content {
              flex: 1;
              margin-left: 2mm;
            }
            .text-line {
              font-weight: bold;
              line-height: 1.2;
              margin-bottom: 0.5mm;
            }
          </style>
        </head>
        <body>
          <div style="display: flex;">
            <div class="qr-code" id="qrcode"></div>
            <div class="content">
              <div class="text-line">${data.barcode || ""}</div>
              <div class="text-line">${data.billNo || ""}</div>
              <div class="text-line">${data.terminalCustomerName || ""}</div>
              <div class="text-line">${data.receiveTime || ""}</div>
            </div>
          </div>
          <scr" + "ipt>
            // 生成二维码
            var qrcode = new QRCode(document.getElementById('qrcode'), {
              text: ${data.barcode},
              width: 60,
              height: 60,
              colorDark: '#000000',
              colorLight: '#ffffff',
              correctLevel: QRCode.CorrectLevel.M
            });
            
            // 打印完成后关闭
            window.onafterprint = function() {
              window.close();
            };
            
            // 自动打印
            setTimeout(function() {
              window.print();
            }, 500);
          </scr" + "ipt>
        </body>
        </html>
      `;
      },
    
      // 使用ActiveX打印
      printWithActiveX(job) {
        try {
          const btApp = new ActiveXObject("BarTender.Application");
          const btFormat = btApp.Formats.Open(job.template, false, "");
        
          // 设置字段值
          this.fieldMappings.forEach(mapping => {
            if (job.data[mapping.key]) {
              btFormat.SetNamedSubStringValue(mapping.key, job.data[mapping.key]);
            }
          });
        
          // 设置打印机
          btFormat.PrintSetup.Printer = job.printer;
          btFormat.PrintSetup.IdenticalCopiesOfLabel = 1;
        
          // 执行打印
          btFormat.PrintOut(true, false);
        
          // 关闭
          btFormat.Close(0);
          btApp.Quit();
        } catch (error) {
          throw new Error("BarTender ActiveX打印失败: " + error.message);
        }
      },
    
      // 降级打印方法
      fallbackPrint(data) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(this.formatForBrowserPrint(data));
        printWindow.document.close();
      },
    
      // 显示结果
      showResult(type, message) {
        this.printResult = { type, message };
        setTimeout(() => {
          this.clearResult();
        }, 5000);
      },
    
      // 清除结果
      clearResult() {
        this.printResult = null;
      },
    },
  };
</script>

<style scoped>
.bartender-printer {
  font-family: Arial, sans-serif;
}

.config-panel {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  width: 600px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.config-panel h3 {
  margin: 0 0 20px 0;
  color: #333;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
}

.config-section {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.config-section label {
  min-width: 100px;
  font-weight: bold;
  color: #555;
}

.config-input, .config-select {
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.file-input-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-input-container .config-input {
  flex: 1;
}

.btn-file-select {
  padding: 8px 12px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
  white-space: nowrap;
}

.btn-file-select:hover {
  background: #5a6268;
}

.config-select {
  cursor: pointer;
}

.checkbox-group {
  flex-direction: column;
  align-items: flex-start;
}

.checkbox-group label {
  min-width: auto;
  margin-bottom: 5px;
  cursor: pointer;
}

.field-mapping {
  flex: 1;
}

.field-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.field-item span {
  min-width: 80px;
  font-size: 13px;
}

.field-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
}

.config-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
}

.btn-test, .btn-save, .btn-cancel, .btn-confirm {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-test {
  background: #17a2b8;
  color: white;
}

.btn-test:hover {
  background: #138496;
}

.btn-save {
  background: #28a745;
  color: white;
}

.btn-save:hover {
  background: #218838;
}

.btn-cancel, .btn-clear {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover, .btn-clear:hover {
  background: #5a6268;
}

.btn-confirm {
  background: #007bff;
  color: white;
}

.btn-confirm:hover {
  background: #0056b3;
}

.preview-panel {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  max-width: 800px;
}

.preview-panel h3 {
  margin: 0 0 20px 0;
  color: #333;
}

.preview-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #eee;
  padding: 10px;
  margin-bottom: 20px;
}

.preview-item {
  display: inline-block;
  margin: 10px;
  border: 1px dashed #ccc;
  padding: 10px;
}

.preview-label {
  width: 80mm;
  height: 30mm;
  display: flex;
  align-items: center;
  font-size: 9pt;
}

.preview-qr {
  width: 15mm;
  height: 15mm;
  margin-right: 2mm;
}

.qr-code {
  width: 100%;
  height: 100%;
}

.preview-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.preview-text {
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 0.5mm;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.printing-status {
  text-align: center;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.print-result {
  padding: 15px;
  margin: 20px 0;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.print-result.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.print-result.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.print-result.warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}
</style>